import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "src/redux/hooks";
import { insertValues } from "src/redux/utils/insertValues";

const SECURITY_STATUS_TAG = "vl-sec";
const BAG_SEARCH_TAG = "vl-bagSearched";
const BAG_SEARCH_FEATURE = "vl-bagSearchPopup";

const getColValueByTag = (row: any, tag: string) => {
  const col = Object.values(row?.cols ?? {}).find(
    (item: any) => item?.tag === tag,
  ) as any;
  return col?.value;
};

interface ValidationResult {
  blocked: true;
  message: string;
}

/**
 * Hook to validate security bag-search requirements before adding a row.
 * Returns null if validation passes, or a blocking result with message if requirement not met.
 */
const useSecurityBagSearchValidation = () => {
  const { getValues } = useFormContext();
  const { sections } = useSelector(state => state.generatorSlice.lbentry);

  const validateBagSearchRequired = useCallback(
    (sectionIndex: number): ValidationResult | null => {
      const section = sections[sectionIndex];
      if (
        !section?.feature?.includes(BAG_SEARCH_FEATURE) ||
        !section.securityThresholds
      ) {
        return null;
      }

      const nextVisitorNumber = section.rows.length + 1;
      const thresholdValues = Object.values(section.securityThresholds)
        .map(value => Number(value))
        .filter(value => Number.isFinite(value) && value > 0);

      if (!thresholdValues.length) return null;

      const lowestThreshold = Math.min(...thresholdValues);
      if (nextVisitorNumber < lowestThreshold) return null;

      // Find security section
      const securitySectionIndex = sections.findIndex(sec =>
        sec.headers?.some(header => header.tag === SECURITY_STATUS_TAG),
      );

      if (securitySectionIndex === -1) return null;

      // Get live form values
      const data = getValues();
      const [liveSecuritySection, liveCurrentSection]: any = insertValues(
        data,
        [sections[securitySectionIndex], section],
      );

      // Extract security status value
      const securityValue = getColValueByTag(
        liveSecuritySection?.rows?.[0],
        SECURITY_STATUS_TAG,
      )?.key;

      if (!securityValue) return null;

      const thresholdForLevel =
        section.securityThresholds[String(securityValue)];
      const threshold = Number(thresholdForLevel);

      if (!Number.isFinite(threshold) || threshold <= 0) return null;
      if (nextVisitorNumber < threshold) return null;

      const expectedBagSearches = Math.floor(section.rows.length / threshold);
      const actualBagSearches = (liveCurrentSection.rows ?? []).filter(
        (row: any) => getColValueByTag(row, BAG_SEARCH_TAG) === true,
      ).length;

      if (actualBagSearches < expectedBagSearches) {
        return {
          blocked: true,
          message:
            "Based on your current security status, a bag search must be selected and completed before you can continue with this entry.",
        };
      }

      return null;
    },
    [sections, getValues],
  );

  return { validateBagSearchRequired };
};

export default useSecurityBagSearchValidation;
